import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Camera, Loader2, Plus, Receipt } from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "@/components/common/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const today = () => new Date().toLocaleDateString("sv-SE");

const ReceiptScan = ({ company, v, setV, onOpen }) => {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    onOpen?.();
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/business/companies/${company.id}/transactions/scan`, fd);
      const s = data.suggestion;
      setV((cur) => ({ ...cur, kind: "expense", receipt_id: data.receipt_id, ...(s ? { label: s.label || cur.label, date: s.date || cur.date, amount: s.amount != null ? String(s.amount) : cur.amount, category: s.category || cur.category } : {}) }));
      if (s) toast.success("Ticket lu : vérifiez puis cliquez sur Ajouter"); else toast.message("Ticket joint mais illisible : complétez les champs");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <Button asChild variant="outline" size="sm" className="rounded-full" disabled={busy}>
      <label data-testid="biz-tx-scan">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Camera className="mr-1 h-4 w-4" />}{busy ? "Lecture du ticket…" : v?.receipt_id ? "Changer le ticket" : "Photo d'un ticket"}
        <input type="file" accept="image/*,application/pdf" capture="environment" className="sr-only" onChange={onFile} disabled={busy} data-testid="biz-tx-scan-input" /></label>
    </Button>
  );
};

export const ManualTxForm = ({ company, cats }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [v, setV] = useState({ kind: "expense", date: today(), label: "", amount: "", category: "", receipt_id: null });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/business/companies/${company.id}/transactions`, { ...v, amount: Number(v.amount), receipt_id: v.kind === "expense" ? v.receipt_id : null, category: v.kind === "expense" ? v.category || null : null });
      ["biz-tx", "biz-overview", "biz-dashboard", "biz-breakeven", "biz-score"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(v.kind === "expense" ? "Dépense ajoutée" : "Recette ajoutée");
      setV({ ...v, label: "", amount: "", receipt_id: null });
    } catch (err) { toast.error(formatApiError(err)); }
  };
  if (!open) return (
    <div className="flex flex-wrap gap-2"><Button variant="outline" size="sm" className="rounded-full" onClick={() => setOpen(true)} data-testid="biz-tx-add-open"><Plus className="mr-1 h-4 w-4" />Saisir une dépense ou une recette</Button>
      <ReceiptScan company={company} v={v} setV={setV} onOpen={() => setOpen(true)} /></div>
  );
  return (
    <form onSubmit={submit} className="space-y-3 rounded-xl border border-border bg-muted/30 p-4" data-testid="biz-tx-add-form">
      <div className="flex flex-wrap items-center gap-1.5">{[["expense", "Dépense"], ["income", "Recette"]].map(([k, l]) => (
        <button key={k} type="button" onClick={() => setV({ ...v, kind: k })} data-testid={`biz-tx-kind-${k}`}
          className={cn("rounded-full border px-3 py-1 text-xs font-semibold transition-colors", v.kind === k ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted")}>{l}</button>))}
        <span className="ml-auto"><ReceiptScan company={company} v={v} setV={setV} /></span>
        {v.receipt_id && <span className="flex w-full items-center gap-1 text-xs text-muted-foreground" data-testid="biz-tx-receipt-attached"><Receipt className="h-3 w-3" />Ticket joint à la dépense</span>}</div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <DatePicker value={v.date} onChange={(e) => setV({ ...v, date: e.target.value })} noFuture required placeholder="Date" data-testid="biz-tx-add-date" />
        <Input value={v.label} onChange={(e) => setV({ ...v, label: e.target.value })} placeholder="Libellé (ex. Fournitures bureau)" required minLength={2} maxLength={120} data-testid="biz-tx-add-label" />
        <Input type="number" min="0.01" step="0.01" value={v.amount} onChange={(e) => setV({ ...v, amount: e.target.value })} placeholder="Montant TTC €" required data-testid="biz-tx-add-amount" />
        {v.kind === "expense" && <Select value={v.category} onValueChange={(c) => setV({ ...v, category: c })}><SelectTrigger data-testid="biz-tx-add-category"><SelectValue placeholder="Catégorie" /></SelectTrigger>
          <SelectContent>{Object.entries(cats).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>}
      </div>
      <div className="flex gap-2"><Button type="submit" size="sm" className="rounded-full" data-testid="biz-tx-add-submit">Ajouter</Button>
        <Button type="button" size="sm" variant="ghost" className="rounded-full" onClick={() => setOpen(false)} data-testid="biz-tx-add-close">Fermer</Button></div>
    </form>
  );
};
